import { memo, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import styled from 'styled-components'
import { Layout } from 'components/Layout'
import { CurrentUserContextProvider } from 'context/useCurrentUser'
import { ARC_HERO_WASH } from 'themes/ArcTheme'
import { ProjectFormProvider } from './context/ProjectFormContext'
import { SelectionView } from './views/SelectionView'
import { AIFlowView } from './views/AIFlowView'
import { ManualFlowView } from './views/ManualFlowView'

/**
 * Full-viewport arcmosaico.ai hero wash behind the whole creation flow
 * (AR-797). Fixed and behind the content, so navbar/sidebar/cards paint on top.
 */
const HeroWashBackground = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background-image: ${ARC_HERO_WASH};
`

type FlowType = 'selection' | 'ai' | 'manual'

const getInitialFlowType = (isEditMode: boolean): FlowType => {
  if (isEditMode) return 'manual'

  // In create mode, only #details allows direct access to manual flow
  // #skills requires projectId (only available after completing step 1)
  const hash = window.location.hash.slice(1)
  if (hash === 'details') return 'manual'

  return 'selection'
}

export const ArcNewProject = memo(() => {
  const { id: urlProjectId } = useParams<{ id: string }>()
  const isEditMode = !!urlProjectId
  const [flowType, setFlowType] = useState<FlowType>(() => getInitialFlowType(isEditMode))

  const handleBack = useCallback(() => {
    window.history.replaceState(null, '', window.location.pathname)
    setFlowType('selection')
  }, [])

  const handleSelect = useCallback((type: 'ai' | 'manual') => {
    setFlowType(type)
  }, [])

  return (
    <CurrentUserContextProvider>
      <Layout>
        <HeroWashBackground />
        <ProjectFormProvider>
          {flowType === 'selection' && <SelectionView onSelect={handleSelect} />}
          {flowType === 'ai' && <AIFlowView onBack={handleBack} />}
          {flowType === 'manual' && (
            <ManualFlowView isEditMode={isEditMode} projectId={urlProjectId} onBack={handleBack} />
          )}
        </ProjectFormProvider>
      </Layout>
    </CurrentUserContextProvider>
  )
})

ArcNewProject.displayName = 'ArcNewProject'
